/**
 * Utilities for building threshold catalog queries from widget configuration.
 */

import type {
  ApiThresholdCatalogRequest,
  ApiThresholdCatalogSource,
} from '@highimpact-frontend/api-types';

export type {
  ApiThresholdCatalogRequest,
  ApiThresholdCatalogSource,
} from '@highimpact-frontend/api-types';

// -- type definitions --

export type ThresholdSupportedPlotType =
  | 'timeseries'
  | 'scatter'
  | 'ombrothermic'
  | 'bar'
  | 'windrose';

export type ThresholdRepresentationCoordinateSystem =
  | 'time_series'
  | 'cartesian_2d'
  | 'polar_2d';

export type ThresholdTimeUnit =
  | 'millisecondes'
  | 'seconds'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years';

export interface ThresholdFixedTimeRange {
  type: 'fixed_time_range';
  name?: string | null;
  campaign_ids?: number[] | null;
  startdate: string;
  enddate: string;
}

interface ThresholdDynamicTimeRangeBase {
  name?: string | null;
  unit: ThresholdTimeUnit;
  quantity: number;
  period_shift: number | null;
  direction: 'before' | 'after';
  relative_to: string;
}

export interface ThresholdCalendarTimeRange
  extends ThresholdDynamicTimeRangeBase {
  type: 'calendar_time_range';
}

export interface ThresholdRelativeTimeRange
  extends ThresholdDynamicTimeRangeBase {
  type: 'relative_time_range';
}

export type ThresholdTimeRange =
  | ThresholdFixedTimeRange
  | ThresholdCalendarTimeRange
  | ThresholdRelativeTimeRange;

export interface ThresholdCatalogQueryBuilderInput {
  plotType: ThresholdSupportedPlotType;
  dataSource: ApiThresholdCatalogSource[];
  timeIntervals: ThresholdTimeRange[];
}

/** @deprecated Use {@link ApiThresholdCatalogRequest} directly. */
export type ThresholdCatalogQuery = ApiThresholdCatalogRequest;

type ResolvedTimeUnit =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

// -- constants --

const PLOT_TO_COORDINATE_SYSTEM = {
  timeseries: 'time_series',
  scatter: 'cartesian_2d',
  ombrothermic: 'cartesian_2d',
  bar: 'cartesian_2d',
  windrose: 'polar_2d',
} satisfies Record<
  ThresholdSupportedPlotType,
  ThresholdRepresentationCoordinateSystem
>;

// -- functions --

function isSupportedPlotType(
  plotType: string
): plotType is ThresholdSupportedPlotType {
  return plotType in PLOT_TO_COORDINATE_SYSTEM;
}

function assertValidSourceBinding(source: ApiThresholdCatalogSource): void {
  if (!Number.isFinite(source.measure_point_id)) {
    throw new Error('Each source must define a valid measure_point_id');
  }

  if (!Number.isFinite(source.measure_type_id)) {
    throw new Error('Each source must define a valid measure_type_id');
  }
}

function assertValidDate(date: Date, label: string): void {
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid date provided for ${label}`);
  }
}

function normalizeTimeRange(timeRange: ThresholdTimeRange): ThresholdTimeRange {
  switch (timeRange.type) {
    case 'fixed_time_range':
      return {
        type: 'fixed_time_range',
        startdate: timeRange.startdate,
        enddate: timeRange.enddate,
        ...(timeRange.campaign_ids
          ? { campaign_ids: [...timeRange.campaign_ids] }
          : {}),
      };
    case 'calendar_time_range':
      return {
        type: 'calendar_time_range',
        unit: timeRange.unit,
        quantity: timeRange.quantity,
        period_shift: timeRange.period_shift,
        direction: timeRange.direction,
        relative_to: timeRange.relative_to,
      };
    case 'relative_time_range':
      return {
        type: 'relative_time_range',
        unit: timeRange.unit,
        quantity: timeRange.quantity,
        period_shift: timeRange.period_shift,
        direction: timeRange.direction,
        relative_to: timeRange.relative_to,
      };
  }
}

function toResolvedTimeUnit(unit: ThresholdTimeUnit): ResolvedTimeUnit {
  switch (unit) {
    case 'millisecondes':
      return 'millisecond';
    case 'seconds':
      return 'second';
    case 'minutes':
      return 'minute';
    case 'hours':
      return 'hour';
    case 'days':
      return 'day';
    case 'weeks':
      return 'week';
    case 'months':
      return 'month';
    case 'years':
      return 'year';
  }
}

function addUtc(date: Date, amount: number, unit: ResolvedTimeUnit): Date {
  const next = new Date(date);

  switch (unit) {
    case 'millisecond':
      next.setUTCMilliseconds(next.getUTCMilliseconds() + amount);
      break;
    case 'second':
      next.setUTCSeconds(next.getUTCSeconds() + amount);
      break;
    case 'minute':
      next.setUTCMinutes(next.getUTCMinutes() + amount);
      break;
    case 'hour':
      next.setUTCHours(next.getUTCHours() + amount);
      break;
    case 'day':
      next.setUTCDate(next.getUTCDate() + amount);
      break;
    case 'week':
      next.setUTCDate(next.getUTCDate() + amount * 7);
      break;
    case 'month':
      next.setUTCMonth(next.getUTCMonth() + amount);
      break;
    case 'year':
      next.setUTCFullYear(next.getUTCFullYear() + amount);
      break;
  }

  return next;
}

function roundDownToUnit(date: Date, unit: ResolvedTimeUnit): Date {
  const next = new Date(date);

  switch (unit) {
    case 'year':
      next.setUTCMonth(0, 1);
      next.setUTCHours(0, 0, 0, 0);
      break;
    case 'month':
      next.setUTCDate(1);
      next.setUTCHours(0, 0, 0, 0);
      break;
    case 'week': {
      next.setUTCHours(0, 0, 0, 0);
      const dayOfWeek = next.getUTCDay();
      const offsetToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      next.setUTCDate(next.getUTCDate() - offsetToMonday);
      break;
    }
    case 'day':
      next.setUTCHours(0, 0, 0, 0);
      break;
    case 'hour':
      next.setUTCMinutes(0, 0, 0);
      break;
    case 'minute':
      next.setUTCSeconds(0, 0);
      break;
    case 'second':
      next.setUTCMilliseconds(0);
      break;
    case 'millisecond':
      break;
  }

  return next;
}

function resolveTimeRangeBounds(timeRange: ThresholdTimeRange): {
  start: Date;
  end: Date;
} {
  if (timeRange.type === 'fixed_time_range') {
    const start = new Date(timeRange.startdate);
    const end = new Date(timeRange.enddate);

    assertValidDate(start, 'timeRange.startdate');
    assertValidDate(end, 'timeRange.enddate');

    return { start, end };
  }

  const reference =
    timeRange.relative_to === 'now'
      ? new Date()
      : new Date(timeRange.relative_to);

  assertValidDate(reference, 'timeRange.relative_to');

  const unit = toResolvedTimeUnit(timeRange.unit);
  const roundedReference = roundDownToUnit(reference, unit);
  const quantity = Math.max(1, Math.floor(timeRange.quantity || 1));
  const periodShift = timeRange.period_shift;

  if (timeRange.direction === 'before') {
    const useCurrentWindow =
      timeRange.relative_to === 'now' &&
      (periodShift == null || periodShift === 0);

    if (useCurrentWindow || periodShift == null) {
      const start = addUtc(roundedReference, -(quantity - 1), unit);
      const candidateEnd = addUtc(roundedReference, 1, unit);
      const end =
        timeRange.relative_to === 'now' &&
        new Date().getTime() < candidateEnd.getTime()
          ? new Date()
          : candidateEnd;

      return { start, end };
    }

    const baseDate =
      timeRange.relative_to === 'now'
        ? roundedReference
        : addUtc(roundedReference, 1, unit);

    return {
      start: addUtc(baseDate, -quantity * (periodShift + 1), unit),
      end: addUtc(baseDate, -quantity * periodShift, unit),
    };
  }

  const start =
    periodShift == null
      ? roundedReference
      : addUtc(roundedReference, quantity * periodShift, unit);
  const end = addUtc(start, quantity, unit);

  return { start, end };
}

function resolveCatalogTimeRange(
  timeIntervals: ThresholdTimeRange[]
): ThresholdTimeRange {
  if (timeIntervals.length === 0) {
    throw new Error('At least one time interval is required');
  }

  if (timeIntervals.length === 1) {
    return normalizeTimeRange(timeIntervals[0]);
  }

  const bounds = timeIntervals.map(resolveTimeRangeBounds);
  const start = new Date(
    Math.min(...bounds.map((range) => range.start.getTime()))
  );
  const end = new Date(Math.max(...bounds.map((range) => range.end.getTime())));

  return {
    type: 'fixed_time_range',
    startdate: start.toISOString(),
    enddate: end.toISOString(),
  };
}

/**
 * Builds the payload expected by the threshold catalog endpoint from a widget's
 * current source selection, plot type, and active time intervals.
 *
 * @param input Widget-derived inputs required by the thresholds catalog API.
 * @returns A request payload ready to be sent to the thresholds catalog endpoint.
 * @throws When the plot type is unsupported or the required inputs are missing.
 *
 * @example
 * const payload = buildThresholdCatalogQuery({
 *   plotType: 'timeseries',
 *   dataSource: [{ measure_point_id: 101, measure_type_id: 39 }],
 *   timeIntervals: [{
 *     type: 'fixed_time_range',
 *     startdate: '2026-01-01T00:00:00.000Z',
 *     enddate: '2026-12-31T23:59:59.000Z',
 *   }],
 * });
 */
export function buildThresholdCatalogQuery(
  input: ThresholdCatalogQueryBuilderInput
): ApiThresholdCatalogRequest {
  if (!isSupportedPlotType(input.plotType)) {
    throw new Error(
      `Unsupported plot type for thresholds catalog: ${input.plotType}`
    );
  }

  if (input.dataSource.length === 0) {
    throw new Error('At least one data source is required');
  }

  input.dataSource.forEach(assertValidSourceBinding);

  return {
    sources: input.dataSource.map((source) => ({ ...source })),
    plot: {
      representation_coordinate_system:
        PLOT_TO_COORDINATE_SYSTEM[input.plotType],
    },
    time_range: resolveCatalogTimeRange(input.timeIntervals),
  };
}

export function resolveThresholdRepresentationCoordinateSystem(
  plotType: ThresholdCatalogQueryBuilderInput['plotType']
): ThresholdRepresentationCoordinateSystem {
  if (!isSupportedPlotType(plotType)) {
    throw new Error(
      `Unsupported plot type for thresholds catalog: ${plotType}`
    );
  }

  return PLOT_TO_COORDINATE_SYSTEM[plotType];
}
